import React, { useEffect, useState } from 'react';
import { API_URL } from '../config/apiConfig';
import { getAuthHeader } from '../utils/tokenManager';

/**
 * 재고 등록 모달
 * 선택된 날짜에 대해 재고수를 일괄 등록/수정하는 기능
 * 향후 마감설정, 최소숙박일 등의 기능 확장 가능하도록 설계
 */
const StockRegistrationModal = ({
  isOpen,
  onClose,
  selectedDates = [],
  productId,
  companyNo,
  userId,
  roomTypeNo, // 객실타입번호 (자원번호)
  roomTypeName, // 객실타입명
  roomProductName, // 객실상품명
  onSuccess,
  // 초기 값 프리필
  initialStockCount,
  initialCutoffEnabled,
  initialCutoffMode,
  initialMinStayEnabled,
  initialMinStayDays,
}) => {
  const [stockCount, setStockCount] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  
  // 추가 설정 상태
  const [enableCutoff, setEnableCutoff] = useState(false); // 마감설정 활성화
  const [cutoffMode, setCutoffMode] = useState(true); // true: 마감, false: 마감 해제
  const [minStayDays, setMinStayDays] = useState(''); // 최소숙박일
  const [enableMinStay, setEnableMinStay] = useState(false); // 최소숙박일 활성화

  // 모달이 열릴 때 초기 값 적용
  useEffect(() => {
    if (!isOpen) return;
    // 재고수
    if (initialStockCount != null && initialStockCount !== '') {
      setStockCount(String(initialStockCount));
    } else {
      setStockCount('');
    }
    // 마감 설정
    const cutoffEnabled = Boolean(initialCutoffEnabled);
    setEnableCutoff(cutoffEnabled);
    if (cutoffEnabled && typeof initialCutoffMode === 'boolean') {
      setCutoffMode(initialCutoffMode);
    } else {
      setCutoffMode(true);
    }
    // 최소 숙박일
    const minStayEnabled = Boolean(initialMinStayEnabled);
    setEnableMinStay(minStayEnabled);
    if (minStayEnabled && initialMinStayDays != null && initialMinStayDays !== '') {
      setMinStayDays(String(initialMinStayDays));
    } else {
      setMinStayDays('');
    }
  }, [isOpen, initialStockCount, initialCutoffEnabled, initialCutoffMode, initialMinStayEnabled, initialMinStayDays]);

  if (!isOpen) return null;

  const handleSave = async () => {
    try {
      // 유효성 검사
      if (!productId || !companyNo || !userId) {
        alert('필수 정보가 없습니다.');
        return;
      }

      

      if (!selectedDates || selectedDates.length === 0) {
        alert('날짜를 선택하세요.');
        return;
      }

      if (!roomTypeNo) {
        alert('객실타입 정보가 없습니다.');
        return;
      }

      // 재고수 입력 검증 (마감/최소투숙만 하는 경우 재고수 불필요)
      const digits = String(stockCount || '').replace(/[^0-9]/g, '');
      if (!enableCutoff && !enableMinStay && !digits) {
        alert('재고수를 입력하세요.');
        return;
      }

      // 최소 투숙일 검증
      const minStayVal = String(minStayDays || '').replace(/[^0-9]/g, '');
      if (enableMinStay) {
        if (!minStayVal) {
          alert('최소 투숙일을 입력하세요.');
          return;
        }
        if (Number(minStayVal) < 1) {
          alert('최소 투숙일은 1 이상이어야 합니다.');
          return;
        }
      }

      setIsSaving(true);

      // 선택행사 데이터 구성
      const selectedEvents = selectedDates.map(dateStr => ({
        날짜: dateStr,
        시간: '00:00',
        자원번호: Number(roomTypeNo)
      }));

      let successMessage = '';
      let hasError = false;

      // 1. 재고 등록 처리 (재고수가 입력된 경우)
      if (digits) {
        const stockPayload = {
          Table1: [
            {
              컬럼명: '@상품번호',
              값: String(productId)
            },
            {
              컬럼명: '@연동객실',
              값: ''
            },
            {
              컬럼명: '@시리즈재고업데이트',
              값: false
            },
            {
              컬럼명: '@재고수',
              값: String(digits)
            },
            {
              컬럼명: '@처리자',
              값: String(userId)
            },
            {
              컬럼명: '@업체번호',
              값: String(companyNo)
            },
            {
              컬럼명: '@선택행사',
              값: JSON.stringify(selectedEvents)
            },
            {
              컬럼명: '@캘린더용',
              값: true
            },
            {
              컬럼명: '@컷오프정보',
              값: '[]'
            }
          ]
        };
const stockUrl = `${API_URL}?procedureName=DBO.TRP_T_%ED%88%AC%EC%96%B4%EC%84%B8%EC%85%98%EA%B4%80%EB%A6%AC_%EC%84%A0%ED%83%9DDATA%EC%9E%AC%EA%B3%A0%EC%88%98%EC%A0%95_%ED%98%B8%ED%85%94_01`;
        const stockResponse = await fetch(stockUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...getAuthHeader()
          },
          body: JSON.stringify(stockPayload)
        });

        if (!stockResponse.ok) {
          const errorText = await stockResponse.text();
          hasError = true;
          successMessage += `❌ 재고 등록 실패: ${stockResponse.status}\n`;
        } else {
          const result = await stockResponse.json();
successMessage += `✅ 재고 등록 완료 (${selectedDates.length}개 날짜)\n`;
        }
      }

      // 2. 마감 설정 처리 (마감 설정이 활성화된 경우)
      if (enableCutoff) {
        const cutoffPayload = {
          Table1: [
            {
              컬럼명: '@상품번호',
              값: String(productId)
            },
            {
              컬럼명: '@시리즈적용여부',
              값: '1'
            },
            {
              컬럼명: '@처리자',
              값: String(userId)
            },
            {
              컬럼명: '@업체번호',
              값: String(companyNo)
            },
            {
              컬럼명: '@마감여부',
              값: cutoffMode
            },
            {
              컬럼명: '@선택행사',
              값: JSON.stringify(selectedEvents)
            },
            {
              컬럼명: '@캘린더용',
              값: true
            }
          ]
        };
const cutoffUrl = `${API_URL}?procedureName=DBO.TRP_T_%ED%88%AC%EC%96%B4%EC%84%B8%EC%85%98%EA%B4%80%EB%A6%AC_%EC%84%A0%ED%83%9DDATA%EB%A7%88%EA%B0%90%EC%84%A4%EC%A0%95_03`;
        const cutoffResponse = await fetch(cutoffUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...getAuthHeader()
          },
          body: JSON.stringify(cutoffPayload)
        });

        if (!cutoffResponse.ok) {
          const errorText = await cutoffResponse.text();
          hasError = true;
          successMessage += `❌ 마감 ${cutoffMode ? '설정' : '해제'} 실패: ${cutoffResponse.status}\n`;
        } else {
          const result = await cutoffResponse.json();
successMessage += `✅ 마감 ${cutoffMode ? '설정' : '해제'} 완료 (${selectedDates.length}개 날짜)\n`;
        }
      }

      // 3. 최소 숙박일 설정 처리 (활성화된 경우)
      if (enableMinStay) {
        const minStayVal = String(minStayDays || '').replace(/[^0-9]/g, '');
        const minStayPayload = {
          Table1: [
            { 컬럼명: '@최소투숙일', 값: String(minStayVal) },
            { 컬럼명: '@처리자', 값: String(userId) },
            { 컬럼명: '@선택행사', 값: JSON.stringify(selectedEvents) },
            { 컬럼명: '@업체번호', 값: String(companyNo) },
            { 컬럼명: '@상품번호', 값: String(productId) },
            { 컬럼명: '@캘린더용', 값: true }
          ]
        };
const minStayUrl = `${API_URL}?procedureName=DBO.TRP_T_%EC%B5%9C%EC%86%8C%ED%88%AC%EC%88%99%EC%9D%BC%EC%84%A4%EC%A0%95_%EC%84%A0%ED%83%9DDATA_01`;
        const minStayResponse = await fetch(minStayUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
          body: JSON.stringify(minStayPayload)
        });

        if (!minStayResponse.ok) {
          const errorText = await minStayResponse.text();
          hasError = true;
          successMessage += `❌ 최소 숙박일 설정 실패: ${minStayResponse.status}\n`;
        } else {
          const result = await minStayResponse.json();
successMessage += `✅ 최소 숙박일 설정 완료 (${selectedDates.length}개 날짜)\n`;
        }
      }

      // 결과 표시
      if (hasError) {
        alert(successMessage || '일부 작업이 실패했습니다.');
      } else {
        alert(successMessage || '작업이 완료되었습니다.');
      }
      
      // 초기화
      setStockCount('');
      setEnableCutoff(false);
      setCutoffMode(true);
      setEnableMinStay(false);
      setMinStayDays('');
      
      // 성공 콜백 호출
      if (typeof onSuccess === 'function') {
        onSuccess();
      }
      
      // 모달 닫기
      onClose();

    } catch (error) {
      console.error('❌ 재고 등록 오류:', error);
      alert(`재고 등록 실패: ${error.message}`);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-no-clear="true">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        {/* 헤더 */}
        <div className="border-b px-6 py-4 flex items-center justify-between sticky top-0 bg-white">
          <h3 className="text-lg font-bold flex items-center gap-2">
         
            재고 및 마감 관리
          </h3>
          <button 
            onClick={onClose} 
            className="p-2 hover:bg-gray-100 rounded transition-colors"
            disabled={isSaving}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* 본문 */}
        <div className="p-6 space-y-4">
          {/* 적용 대상 정보 */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h4 className="text-sm font-semibold text-blue-900 mb-2"> 적용 대상</h4>
       <div className="flex items-center gap-6 text-sm">
  <div className="flex items-center gap-2">
    <span className="text-gray-600">객실 타입:</span>
    <span className="font-semibold text-gray-800">{roomTypeName || '-'}</span>
  </div>
  <div className="flex items-center gap-2">
    <span className="text-gray-600">객실 상품:</span>
    <span className="font-semibold text-gray-800">{roomProductName || '-'}</span>
  </div>
</div>

          </div>

          {/* 재고수 입력 */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              재고수 {!enableCutoff && <span className="text-red-500">*</span>}
            </label>
            <input
              type="text"
              inputMode="numeric"
              className="w-full border border-gray-300 rounded-lg px-4 py-3 text-xs font-semibold focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              value={stockCount}
              onChange={(e) => setStockCount(e.target.value.replace(/[^0-9]/g, ''))}
              placeholder="재고수를 입력하세요"
              disabled={isSaving}
            />
            
          </div>

          {/* 추가 설정 */}
          <div className="border-t pt-4 space-y-3">
            <h4 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
              </svg>
              추가 설정
            </h4>
            
            {/* 마감 설정 */}
            <div className={`rounded-lg p-3 border-2 transition-all ${enableCutoff ? 'bg-red-50 border-red-300' : 'bg-gray-50 border-gray-200'}`}>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={enableCutoff}
                  onChange={(e) => setEnableCutoff(e.target.checked)}
                  className="rounded w-4 h-4"
                  disabled={isSaving}
                />
                <span className="text-sm font-semibold text-gray-800">마감 설정/해제</span>
              </label>
              {enableCutoff && (
                <div className="mt-3 ml-6 space-y-2">
                  <div className="flex gap-3">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        checked={cutoffMode === true}
                        onChange={() => setCutoffMode(true)}
                        className="w-4 h-4"
                        disabled={isSaving}
                      />
                      <span className="text-sm font-medium text-red-700">🔒 마감</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        checked={cutoffMode === false}
                        onChange={() => setCutoffMode(false)}
                        className="w-4 h-4"
                        disabled={isSaving}
                      />
                      <span className="text-sm font-medium text-green-700">🔓 마감 해제</span>
                    </label>
                  </div>
                  <p className="text-xs text-gray-600">
                    {cutoffMode ? '선택된 날짜를 판매 마감 처리합니다.' : '선택된 날짜의 마감을 해제합니다.'}
                  </p>
                </div>
              )}
            
            </div>

            {/* 최소 숙박일 */}
       <div
  className={`rounded-lg p-3 border-2 transition-all ${
    enableMinStay ? 'bg-emerald-50 border-emerald-300' : 'bg-gray-50 border-gray-200'
  }`}
>
  <label className="flex items-center gap-2 cursor-pointer mb-2">
    <input
      type="checkbox"
      checked={enableMinStay}
      onChange={(e) => setEnableMinStay(e.target.checked)}
      className="rounded w-4 h-4"
      disabled={isSaving}
    />
    <span className="text-sm font-semibold text-gray-800">최소 숙박일 지정</span>
  </label>

  {enableMinStay && (
    <>
      <div className="ml-6 flex items-center gap-2">
        <input
          type="text"
          inputMode="numeric"
          className="w-32 border border-gray-300 rounded px-3 py-2 text-sm"
          value={minStayDays}
          onChange={(e) => setMinStayDays(e.target.value.replace(/[^0-9]/g, ''))}
          placeholder="예: 2"
          disabled={isSaving}
        />
        <span className="text-xs text-gray-600">일</span>
      </div>
      <p className="text-xs text-gray-500 mt-1 ml-6">
        1일로 입력하면 최소 숙박일 지정이 해제됩니다.
      </p>
    </>
  )}
</div>

          </div>

      
        </div>

        {/* 푸터 */}
        <div className="border-t px-6 py-4 flex justify-end gap-3 sticky bottom-0 bg-white">
          <button
            onClick={onClose}
            className="px-5 py-2.5 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50 transition-colors"
            disabled={isSaving}
          >
            취소
          </button>
          <button
            onClick={handleSave}
            className="px-5 py-2.5 bg-emerald-600 text-white rounded-lg font-medium hover:bg-emerald-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            disabled={isSaving || (!stockCount && !enableCutoff && !enableMinStay)}
          >
            {isSaving ? (
              <>
                <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                처리 중...
              </>
            ) : (
              <>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                {enableCutoff && stockCount
                  ? '재고 등록 및 마감 처리'
                  : enableCutoff
                  ? '마감 처리'
                  : enableMinStay && !stockCount
                  ? '최소 숙박일 설정'
                  : '재고 등록'}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default StockRegistrationModal;
